import { ArrowLeft, Home } from "lucide-react";
import {
  isRouteErrorResponse,
  Link,
  useNavigate,
  useRouteError,
} from "react-router";

import { useDocumentTitle } from "../use-document-title";
import styles from "./error-page.module.css";
import { WaveBackground } from "./wave-background";

/**
 * Route-level `errorElement` for each leaf route (not the root route) so a
 * render error inside a page still leaves `<RootLayout>` (nav rail, header,
 * identity control) on screen — only the page content area shows the error.
 * NOT built on `<StubPage>` — see not-found.tsx's identical note; this page
 * mirrors that one's fully-centered, WaveBackground-backed layout, matching
 * the real reference error page from a sibling app in this monorepo.
 */
export function ErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();
  const status = isRouteErrorResponse(error) ? error.status : undefined;
  const message = isRouteErrorResponse(error)
    ? error.statusText || "Something went wrong rendering this page."
    : error instanceof Error
      ? error.message
      : "Something went wrong rendering this page.";
  const code = status ? String(status) : "Error";

  useDocumentTitle("Something went wrong");

  return (
    <div className={styles.page}>
      <WaveBackground />
      <div className={styles.visual}>
        <p className={styles.code} aria-hidden="true">
          {code}
        </p>
        <div className={styles.bar} aria-hidden="true" />
        <h1 className={styles.heading}>Something went wrong</h1>
        <p className={styles.message}>{message}</p>
        <Link to="/" className={styles.homeButton}>
          <Home size={16} />
          Go Home
        </Link>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={14} />
          Go Back
        </button>
        <p className={styles.footer}>Error Code: {code}</p>
      </div>
    </div>
  );
}
